import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { get, isEmpty } from 'lodash';
import { useQuery } from 'react-query';
import instance from '@api/axiosInstance';
import getMetricsData from '@src/api/metricContainer';
import {
  comparisonIntervalSelector,
  granularityMatchSelector,
} from '@store/summaryContainer/comparison/selectors';
import { trackQueryError } from '@utils/sensor/apptracker';
import { getPercentageMetricForSeries } from '@utils/metrics/metricHelpers';
import { formatTimeseriesData } from '../utils/format';
import { dodAtom, unitAtom } from '../atoms';
import { buildDodSeries } from '../utils/dodSeriesOption';
import { findAxisIndex } from './utils';
import { DOD_TIME_AXIS, VALUE_AXIS } from '../utils/chartSchema';
import useTimeseriesQuery from './useTimeseriesQuery';
import { DATA_TAGS, useGetData, useSetData } from './useData';

const defaultMetricValue = { total: null, data: [] };
const defaultResponseValue = { totals: {}, data: {} };

// hook to fetch dod data
export function useFetchDod({
  enabled = true,
  useBuildQuery = useTimeseriesQuery,
  dataTag = DATA_TAGS.primaryDod,
}) {
  const interval: any = useSelector(comparisonIntervalSelector);
  const granularityMatch = useSelector(granularityMatchSelector);
  const { query: baseQuery, metrics = [], interval: mainInterval }: any = useBuildQuery();
  const dodEnabled = useAtomValue(dodAtom) && !isEmpty(interval);
  const query = { ...baseQuery, interval: `${interval.from}/${interval.to}` };
  const setDodData = useSetData({ tag: dataTag, defaultValue: defaultResponseValue });

  const {
    isFetching,
    isError,
    data: response,
  } = useQuery(
    ['dod-timeseries', query],
    () => {
      return getMetricsData({ queries: [query] }, {}, instance).then((response) => {
        const { data, totals } = get(response, ['data', '0'], []);
        const formattedData = formatTimeseriesData({
          metrics,
          data,
          interval: { ...mainInterval, ...interval },
        });
        return { data: formattedData, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: 3,
      enabled: enabled && dodEnabled && metrics.length > 0 && granularityMatch,
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: {},
      onError: (err) => {
        trackQueryError({ type: 'dod-series', err });
      },
    },
  );

  const formattedData = !dodEnabled || isEmpty(response) ? defaultResponseValue : response;
  // this state consumed by most components instead of the query hook for performance
  useEffect(() => {
    setDodData(formattedData);
  }, [formattedData]);

  return { data: formattedData, isFetching, isError };
}

export const useDodData = ({ metric = null, dataTag }) => {
  const { data, totals }: any = useGetData({ tag: dataTag, defaultValue: defaultResponseValue });
  if (metric) {
    return metric in data ? { data: data[metric], total: totals[metric] } : defaultMetricValue;
  }
  return { data, totals };
};

// util function to format and inject dod series
export function formatDodSeries(option, data = [], barChart = false) {
  // add or update metric filter series
  const DodChartData = buildDodSeries(data, {
    xAxisIndex: findAxisIndex(option.xAxis, DOD_TIME_AXIS),
    yAxisIndex: findAxisIndex(option.yAxis, VALUE_AXIS),
    barChart,
  });

  return { series: [DodChartData] };
}

function useDodSeries({
  metric,
  instance,
  additionalTriggerDeps = [],
  dataTag = DATA_TAGS.primaryDod,
  barChart = false,
  enabled = false,
}: any) {
  const unit = useAtomValue(unitAtom);

  const selectedMetric = unit === '%' ? getPercentageMetricForSeries(metric) : metric;

  let data = useDodData({ metric, dataTag });

  // workaround to disable a series
  if (!enabled) {
    data = { total: {}, data: [] };
  }

  useEffect(() => {
    if (instance) {
      const option = instance.getOption();
      if (option && option._viewType === 'timeseries') {
        const { total, data: chartData } = data;
        const { series } = formatDodSeries(option, chartData, barChart);
        instance.setOption({ series });
      }
    }
  }, [instance, selectedMetric, data, barChart, ...additionalTriggerDeps]);

  // TODO: re-evaluate approach
  const handleZoom = useCallback(
    ({ dataZoomIndex = null }) => {
      // ignore zoom events of dod series
      if (dataZoomIndex === 1) {
        return;
      }

      const dataZoom = instance.getOption().dataZoom[0];
      const { start, end } = dataZoom;

      instance.dispatchAction({
        type: 'dataZoom',
        dataZoomIndex: 1,
        start,
        end,
      });
    },
    [instance],
  );

  // register chart event to watch zoom action
  // mirror it to zoom on dod series
  // zoom axis is always updated even if dod is not enabled, so enabled dod will plot it to zoom level if any
  useEffect(() => {
    if (instance) {
      // TODO: this event is not getting bound to the specified series
      instance.on('dataZoom', { seriesId: 'metric-series', name: 'Primary' }, handleZoom);
      // off on unmount
      return () => {
        instance.off('dataZoom', handleZoom);
      };
    }
  }, [handleZoom, instance]);
}

export default useDodSeries;
